<template>
  <section class="page">
    <div class="page__header">
      <h2 class="page__title">{{ $t('kps.ddMembers.title') }}</h2>
    </div>

    <!-- РЕЕСТР ЧЛЕНОВ ДД (READ-ONLY) -->
    <div class="section-card">
      <DataTable
          :columns="visibleMemberColumns"
          :rows="memberRows"
          :rowKey="rowKey"
          :headerHeight="56"
          :rowHeight="64"
      >
        <!-- ячейки реестра членов ДД (показываем только текст, редактирование отключено UI) -->
        <template #cell-idx="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.idx }}</div>
          <input v-else class="tbl-input" v-model="row.idx" />
        </template>
        <template #cell-fullName="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.fullName }}</div>
          <input v-else class="tbl-input" v-model="row.fullName" />
        </template>
        <template #cell-birthDate="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.birthDate ? formatIsoToDisplay(row.birthDate) : row.birthYear }}</div>
          <DatePicker
            v-else
            v-model="row.birthDate"
            format="iso"
            @update:modelValue="row.birthYear = String((row.birthDate || '').slice(0, 4))"
          />
        </template>
        <template #cell-residence="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.residence }}</div>
          <input v-else class="tbl-input" v-model="row.residence" />
        </template>
        <template #cell-occupation="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.occupation }}</div>
          <input v-else class="tbl-input" v-model="row.occupation" />
        </template>
        <template #cell-duties="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.duties }}</div>
          <input v-else class="tbl-input" v-model="row.duties" />
        </template>
        <template #cell-acceptedAt="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ formatIsoToDisplay(row.acceptedAt) }}</div>
          <DatePicker v-else v-model="row.acceptedAt" format="iso" />
        </template>
        <template #cell-note="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.note }}</div>
          <input v-else class="tbl-input" v-model="row.note" />
        </template>

        <template #head-actions>
          <div class="tbl__header-text" style="justify-content: flex-end; text-align: right; width: 100%;"></div>
        </template>

        <template #cell-actions="{ row }">
          <div v-if="!readOnly" class="actions">
            <button
              v-if="!row.editing"
              class="actions__btn actions__btn--edit"
              @click="onEdit(row, 'memberRows')"
              :title="$t('common.edit')"
              :aria-label="$t('common.edit')"
            >
              <img class="actions__icon" src="../../assets/images/edit.svg" alt="edit" />
            </button>
            <template v-else>
              <button
                class="actions__btn actions__btn--accept"
                @click="onAccept(row, 'memberRows')"
                :title="$t('common.save')"
                :aria-label="$t('common.save')"
              >
                <img class="actions__icon" src="../../assets/images/accept.svg" alt="accept" />
              </button>
              <button
                class="actions__btn actions__btn--decline"
                @click="onDecline(row)"
                :title="$t('common.cancel')"
                :aria-label="$t('common.cancel')"
              >
                <img class="actions__icon" src="../../assets/images/decline.svg" alt="decline" />
              </button>
            </template>
            <button
              class="actions__btn actions__btn--delete"
              @click="onDelete(row, 'memberRows')"
              :title="$t('common.delete')"
              :aria-label="$t('common.delete')"
            >
              <img class="actions__icon" src="../../assets/images/markdown/trash.svg" alt="delete" />
            </button>
          </div>
        </template>
      </DataTable>

      <div style="margin-top: 8px; display: flex; justify-content: flex-end; padding: 0 12px 12px;">
        <ActionButton v-if="!readOnly" :color="'#2A8A1B'" :text="$t('kps.ddMembers.addMember')" @click="addMemberRow" />
      </div>
    </div>

    <!-- МЕРОПРИЯТИЯ ДД -->
    <div class="section-card">
      <DataTable
          :columns="visibleActivityColumns"
          :rows="activityRows"
          :rowKey="rowKey"
          :headerHeight="56"
          :rowHeight="64"
      >
        <!-- редактируемые ячейки мероприятий ДД -->
        <template #cell-idx="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.idx }}</div>
          <input v-else class="tbl-input" v-model="row.idx" />
        </template>

        <template #cell-date="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ formatIsoToDisplay(row.date) }}</div>
          <DatePicker v-else v-model="row.date" format="iso" />
        </template>

        <template #cell-title="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.title }}</div>
          <input v-else class="tbl-input" v-model="row.title" />
        </template>

        <template #cell-place="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.place }}</div>
          <input v-else class="tbl-input" v-model="row.place" />
        </template>

        <template #cell-attendees="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ String(row.attendees || '') }}</div>
          <input v-else class="tbl-input" v-model="row.attendees" type="number" />
        </template>

        <template #cell-conductor="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.conductor }}</div>
          <SelectField v-else v-model="row.responsible_id" :options="users" :placeholder="$t('common.chooseOption')" />
        </template>

        <template #head-actions>
          <div class="tbl__header-text" style="justify-content: flex-end; text-align: right; width: 100%;"></div>
        </template>

        <template #cell-actions="{ row }">
          <div v-if="!readOnly" class="actions">
            <button
                v-if="!row.editing"
                class="actions__btn actions__btn--edit"
                @click="onEdit(row, 'activityRows')"
                :title="$t('common.edit')"
                :aria-label="$t('common.edit')"
            >
              <img class="actions__icon" src="../../assets/images/edit.svg" alt="edit" />
            </button>
            <template v-else>
              <button
                  class="actions__btn actions__btn--accept"
                  @click="onAccept(row, 'activityRows')"
                  :title="$t('common.save')"
                  :aria-label="$t('common.save')"
              >
                <img class="actions__icon" src="../../assets/images/accept.svg" alt="accept" />
              </button>
              <button
                  class="actions__btn actions__btn--decline"
                  @click="onDecline(row)"
                  :title="$t('common.cancel')"
                  :aria-label="$t('common.cancel')"
              >
                <img class="actions__icon" src="../../assets/images/decline.svg" alt="decline" />
              </button>
            </template>
            <button
                class="actions__btn actions__btn--delete"
                @click="onDelete(row, 'activityRows')"
                :title="$t('common.delete')"
                :aria-label="$t('common.delete')"
            >
              <img class="actions__icon" src="../../assets/images/markdown/trash.svg" alt="delete" />
            </button>
          </div>
        </template>
      </DataTable>

      <div style="margin-top: 8px; display: flex; justify-content: flex-end; padding: 0 12px 12px;">
        <ActionButton v-if="!readOnly" :color="'#2A8A1B'" :text="$t('kps.ddMembers.addActivity')" @click="addActivityRow" />
      </div>
    </div>

    <div class="page__footer">
      <ViewNavButtons
          :prev-text="$t('common.back')"
          :next-text="$t('common.next')"
          :prev-to="{ path: '/kps/inspections' }"
          :next-to="{ path: '/kps/plan-extract' }"
      />
    </div>

    <transition name="toast-slide">
      <div v-if="showToast" class="toast" :class="toastMessage.includes('✗') ? 'toast-error' : 'toast-success'">
        <div class="toast-content">{{ toastMessage }}</div>
      </div>
    </transition>
  </section>
</template>

<script>
import DataTable from '@/components/table/DataTable.vue'
import ViewNavButtons from '@/components/navigation/ViewNavButtons.vue'
import ActionButton from '@/components/widgets/ActionButton.vue'
import MaskedInput from '@/components/widgets/MaskedInput.vue'
import SelectField from '@/components/widgets/SelectField.vue'
import DatePicker from '@/components/widgets/DatePicker.vue'
import { checkOrCreateBookByDate } from '@/api/book'
import { listDdMembers, createDdMember, updateDdMember, deleteDdMember } from '@/api/ddMembers'
import { listDdActivities, createDdActivity, updateDdActivity, deleteDdActivity } from '@/api/ddActivities'
import { http } from '@/api/http'

import { ensureCurrentUser, getCachedCurrentUser } from '@/api/user'
import { canEdit } from '@/access/armAccess'

export default {
  name: 'KpsDdMembersView',
  components: { DataTable, ViewNavButtons, MaskedInput, SelectField, DatePicker , ActionButton },
  data() {
    return {
      currentUser: null,
      memberRows: [],
      activityRows: [],
      bookId: null,
      users: [],

      toastMessage: '',
      showToast: false,
    }
  },
  computed: {
    canEditKps() {
      return canEdit(this.currentUser || getCachedCurrentUser(), '/kps/section-4')
    },
    readOnly() { return !this.canEditKps },
    memberColumns() {
      return [
        { key: 'idx', label: this.$t('kps.ddMembers.colIdx'), width: 97 },
        { key: 'fullName', label: this.$t('kps.ddMembers.colFullName'), width: 176 },
        { key: 'birthDate', label: this.$t('kps.ddMembers.colBirthDate'), width: 175 },
        { key: 'residence', label: this.$t('kps.ddMembers.colResidence'), width: 108 },
        { key: 'occupation', label: this.$t('kps.ddMembers.colOccupation'), width: 145 },
        { key: 'duties', label: this.$t('kps.ddMembers.colDuties'), width: 142 },
        { key: 'acceptedAt', label: this.$t('kps.ddMembers.colAcceptedAt'), width: 210 },
        { key: 'note', label: this.$t('kps.ddMembers.colNote'), width: 119 },
        { key: 'actions', label: '', width: 160 },
      ]
    },
    activityColumns() {
      return [
        { key: 'idx', label: this.$t('kps.ddMembers.colIdx'), width: 73 },
        { key: 'date', label: this.$t('kps.ddMembers.colActDate'), width: 170 },
        { key: 'title', label: this.$t('kps.ddMembers.colActTitle'), width: 303 },
        { key: 'place', label: this.$t('kps.ddMembers.colActPlace'), width: 125 },
        { key: 'attendees', label: this.$t('kps.ddMembers.colActAttendees'), width: 149 },
        { key: 'conductor', label: this.$t('kps.ddMembers.colActConductor'), width: 226 },
        { key: 'actions', label: '', width: 150 },
      ]
    },
    visibleMemberColumns() {
      return this.readOnly ? this.memberColumns.filter(c => c.key !== 'actions') : this.memberColumns;
    },
    visibleActivityColumns() {
      return this.readOnly ? this.activityColumns.filter(c => c.key !== 'actions') : this.activityColumns;
    },
  },
  async mounted() {
    try { this.currentUser = await ensureCurrentUser() }
    catch { this.currentUser = getCachedCurrentUser() }
    this.initAndFetch()
  },
  methods: {
    showSuccessToast(message) {
      this.toastMessage = message
      this.showToast = true
      clearTimeout(this.__toastTimer)
      this.__toastTimer = setTimeout(() => { this.showToast = false }, 2500)
    },
    showErrorToast(message) {
      this.toastMessage = message
      this.showToast = true
      clearTimeout(this.__toastTimer)
      this.__toastTimer = setTimeout(() => { this.showToast = false }, 3500)
    },
    getNextIdx(list) {
      const nums = (Array.isArray(list) ? list : [])
        .map((r) => parseInt(String(r?.idx ?? '').trim(), 10))
        .filter((n) => Number.isFinite(n) && n > 0)
      const max = nums.length ? Math.max(...nums) : 0
      return String(max + 1)
    },
    fillMissingIdx(list) {
      if (!Array.isArray(list)) return
      list.forEach((row, i) => {
        if (!String(row.idx || '').trim()) row.idx = String(i + 1)
      })
    },
    async initAndFetch() {
      try {
        const d = new Date()
        const two = (n) => (n < 10 ? `0${n}` : String(n))
        const dateISO = `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`
        const book = await checkOrCreateBookByDate(dateISO)
        this.bookId = Array.isArray(book) ? book[0].id : book.id
        await Promise.all([this.fetchMembers(), this.fetchActivities(), this.fetchUsers()])
      } catch (e) {
        console.error('DD Members: init/fetch failed', e)
      }
    },

    async fetchMembers() {
      if (!this.bookId) return
      try {
        const list = await listDdMembers({ book: this.bookId })
        if (Array.isArray(list)) {
          this.memberRows = list.map((it) => ({
            id: it.id,
            idx: it.idx ?? '',
            fullName: it.full_name ?? it.fullName ?? '',
            birthDate: it.birth_date ?? it.birthDate ?? '',
            birthYear: it.birth_year ?? it.birthYear ?? '',
            residence: it.residence ?? '',
            occupation: it.occupation ?? '',
            duties: it.duties ?? '',
            acceptedAt: it.accepted_at ?? it.acceptedAt ?? '',
            note: it.note ?? '',
            editing: false,
          }))
        }
      } catch (e) {
        console.error('DD Members: list failed', e)
      }
    },

    async fetchActivities() {
      if (!this.bookId) return
      try {
        const list = await listDdActivities({ book: this.bookId })
        if (Array.isArray(list)) {
          this.activityRows = list.map((it) => {
            return {
              id: it.id,
              idx: it.number ?? it.idx ?? '',
              date: it.date ?? '',
              title: it.title ?? '',
              place: it.place ?? '',
              attendees: String(it.attendees_count ?? it.attendees ?? '').trim(),
              conductor: (it.responsible && typeof it.responsible === 'object') ? (it.responsible.full_name || '') : (it.responsible ?? it.responsible_name ?? it.conductor ?? ''),
              responsible_id: it.responsible_id ?? ((it.responsible && typeof it.responsible === 'object') ? it.responsible.id : null),
              editing: false,
            }
          })
          this.fillMissingIdx(this.activityRows)
        }
      } catch (e) {
        console.error('DD Activities: list failed', e)
      }
    },

    async fetchUsers() {
      try {
        const response = await http.get('users/')
        const list = response.data.results || response.data // Обработка пагинации
        if (Array.isArray(list)) {
          this.users = list.map(u => ({
            value: u.id,
            label: u.full_name,
            ...u
          }))
        }
      } catch (e) {
        console.error('Users: list failed', e)
      }
    },

    rowKey(r) {
      return r.__id ?? r.id
    },

    onEdit(row, listKey) {
      row.__backup = JSON.parse(JSON.stringify(row))
      row.editing = true
      void listKey
    },

    async onAccept(row, listKey) {
      try {
        if (!String(row.idx || '').trim()) {
          this.showErrorToast(`${this.$t('common.fillRequired')}: ${this.$t('kps.ddMembers.colIdx')}`)
          return
        }
        const { __id, __backup, editing, ...rest } = row

        if (listKey === 'memberRows') {
          const payload = {
            book: this.bookId,
            idx: row.idx || '',
            full_name: row.fullName || '',
            birth_year: row.birthYear || (row.birthDate ? String(row.birthDate).slice(0, 4) : ''),
            birth_date: row.birthDate || null,
            residence: row.residence || '',
            occupation: row.occupation || '',
            duties: row.duties || '',
            accepted_at: row.acceptedAt || null,
            note: row.note || '',
          }

          if (row.id) {
            await updateDdMember(row.id, payload)
          } else {
            const resp = await createDdMember(payload)
            if (resp && resp.id) row.id = resp.id
          }
        } else if (listKey === 'activityRows') {
          const payload = this.buildActivityPayload(row)

          if (row.id) {
            await updateDdActivity(row.id, payload)
          } else {
            const resp = await createDdActivity({ book: this.bookId, ...payload })
            if (resp && resp.id) row.id = resp.id
          }
        }

        row.editing = false
        delete row.__backup
        
        // Update conductor display name after save
        if (listKey === 'activityRows' && row.responsible_id) {
          const user = this.users.find(u => u.value === row.responsible_id)
          if (user) row.conductor = user.label
        }
        
        // Refresh lists
        if (listKey === 'memberRows') await this.fetchMembers()
        if (listKey === 'activityRows') await this.fetchActivities()

        this.showSuccessToast(this.$t('common.saved'))

      } catch (e) {
        console.error('DD: save failed', e)
        this.showErrorToast(this.$t('common.saveFailed') + ': ' + (e?.message || e))
      }
    },

    onDecline(row) {
      if (row.__backup) {
        const b = row.__backup
        Object.keys(b).forEach((k) => {
          if (k !== '__backup') row[k] = b[k]
        })
        delete row.__backup
      }
      row.editing = false
    },

    async onDelete(row, listKey) {
      const list = this[listKey]
      if (!Array.isArray(list)) return

      const id = row.__id ?? row.id

      if (!row.id) {
        const idx = list.findIndex((r) => (r.__id ?? r.id) === id)
        if (idx !== -1) this[listKey] = [...list.slice(0, idx), ...list.slice(idx + 1)]
        return
      }

      try {
        if (listKey === 'memberRows') await deleteDdMember(row.id)
        if (listKey === 'activityRows') await deleteDdActivity(row.id)

        const idx = list.findIndex((r) => (r.__id ?? r.id) === id)
        if (idx !== -1) this[listKey] = [...list.slice(0, idx), ...list.slice(idx + 1)]

        if (listKey === 'activityRows') this.fillMissingIdx(this[listKey])

        this.showSuccessToast(this.$t('common.deleted'))
      } catch (e) {
        console.error('DD: delete failed', e)
        if (e?.status === 404) {
          const idx = list.findIndex((r) => (r.__id ?? r.id) === id)
          if (idx !== -1) this[listKey] = [...list.slice(0, idx), ...list.slice(idx + 1)]
          this.showSuccessToast(this.$t('common.deleted'))
          return
        }
        this.showErrorToast(this.$t('common.deleteFailed') + ': ' + (e?.message || e))
      }
    },

    addMemberRow() {
      const row = {
        __id: 'ddm-' + Date.now(),
        idx: '',
        fullName: '',
        birthDate: '',
        birthYear: '',
        residence: '',
        occupation: '',
        duties: '',
        acceptedAt: '',
        note: '',
        editing: true,
      }
      this.memberRows = [...this.memberRows, row]
    },

    addActivityRow() {
      const row = {
        __id: 'dda-' + Date.now(),
        idx: this.getNextIdx(this.activityRows),
        date: '',
        title: '',
        place: '',
        attendees: '',
        conductor: '',
        responsible_id: null,
        editing: true,
      }
      this.activityRows = [...this.activityRows, row]
    },

    formatIsoToDisplay(dateStr) {
      if (!dateStr) return ''
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [y, m, d] = dateStr.split('-')
        return `${d}.${m}.${y}`
      }
      return dateStr
    },

    buildActivityPayload(row) {
      return {
        number: row.idx || '',
        date: row.date || '',
        title: row.title || '',
        place: row.place || '',
        attendees_count: row.attendees ? Number(row.attendees) : 0,
        responsible_id: row.responsible_id ?? null,
      }
    },
  },
}
</script>

<style scoped>
.page {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.page__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.page__title {
  margin: 0;
  color: #111827;
  font-size: 24px;
  font-weight: 700;
  line-height: 31.2px;
}

.section-card {
  position: relative;
  background: #ffffff;
  border-radius: 16px;
  padding: 0;
}

.page__footer {
  display: flex;
  justify-content: flex-end;
  margin-top: auto;
}

/* Минимальные стили для CRUD */
.cell-text {
  flex: 1 1 0;
  color: #111827;
  font-size: 16px;
  line-height: 20px;
  width: 100%;
}
.tbl-input {
  width: 100%;
  height: 32px;
  padding: 6px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  line-height: 20px;
  font-family: 'Times New Roman', Times, serif;
  color: #111827;
  background: #fff;
}
.tbl-input:focus {
  outline: 2px solid rgba(47, 120, 238, 0.25);
  outline-offset: 0;
}

/* Prevent date value truncation in narrow cells (Chrome). */
.tbl-input[type="date"] {
  min-width: 150px;
}

.actions {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  justify-content: flex-end;
  width: 100%;
}
.actions__btn {
  width: 30px;
  height: 30px;
  padding: 6px;
  border-radius: 8px;
  border: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.actions__icon {
  width: 16px;
  height: 16px;
  display: block;
}
.actions__btn--edit {
  background: #2f78ee;
}
.actions__btn--accept {
  background: #2a8a1b;
}
.actions__btn--decline {
  background: #d81717;
}
.actions__btn--delete {
  background: #d81717;
}

/* Toast Notifications */
.toast {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  min-width: 220px;
  max-width: 420px;
  padding: 14px 16px;
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
}
.toast-success { background: #2A8A1B; }
.toast-error { background: #D81717; }
.toast-content {
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
}
.toast-slide-enter-active,
.toast-slide-leave-active {
  transition: all 0.3s ease;
}
.toast-slide-enter-from {
  transform: translateY(20px);
  opacity: 0;
}
.toast-slide-leave-to {
  transform: translateY(20px);
  opacity: 0;
}
</style>
